// FILE: src/pages/admin/directory/DirectoryDrivers.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { fetchDirectoryDrivers } from "../../../api/adminDirectoryApi";

/* --------------------- helpers --------------------- */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function cleanStr(x) {
  const s = safeStr(x).trim();
  return s ? s : "";
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function pct01(x, digits = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}
function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
function useDebouncedValue(value, delayMs = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

/* --------------------- UI primitives (match Owners/Users) --------------------- */

function Chip({ tone = "slate", children }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-800 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap",
        cls
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ title, value, hint, accent = "slate" }) {
  const ring =
    accent === "green"
      ? "ring-emerald-100"
      : accent === "red"
      ? "ring-rose-100"
      : accent === "amber"
      ? "ring-amber-100"
      : accent === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  const dot =
    accent === "green"
      ? "bg-emerald-500"
      : accent === "red"
      ? "bg-rose-500"
      : accent === "amber"
      ? "bg-amber-500"
      : accent === "blue"
      ? "bg-sky-500"
      : "bg-slate-700";

  return (
    <div
      className={classNames(
        "rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4 h-[96px] flex flex-col justify-between",
        ring
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">
          {title}
        </div>
        <span className={classNames("h-2 w-2 rounded-full shrink-0", dot)} aria-hidden="true" />
      </div>

      <div className="mt-1 text-2xl font-semibold text-slate-900 truncate">{value}</div>

      {hint ? (
        <div className="mt-1 text-xs text-slate-500 truncate">{hint}</div>
      ) : (
        <div className="mt-1 text-xs text-slate-500 opacity-0">.</div>
      )}
    </div>
  );
}

function meterClasses(tone) {
  switch (tone) {
    case "blue":
      return { track: "bg-sky-100", bar: "bg-gradient-to-r from-sky-600 to-sky-500" };
    case "green":
      return { track: "bg-emerald-100", bar: "bg-gradient-to-r from-emerald-600 to-emerald-500" };
    case "red":
      return { track: "bg-rose-100", bar: "bg-gradient-to-r from-rose-600 to-rose-500" };
    case "amber":
      return { track: "bg-amber-100", bar: "bg-gradient-to-r from-amber-600 to-amber-500" };
    default:
      return { track: "bg-slate-100", bar: "bg-gradient-to-r from-slate-700 to-slate-600" };
  }
}

function Meter({ value01, tone = "slate" }) {
  const w = clamp(Math.round(num(value01) * 100), 0, 100);
  const { track, bar } = meterClasses(tone);

  return (
    <div className={classNames("mt-2 h-2.5 w-full rounded-full overflow-hidden", track)}>
      <div className={classNames("h-full rounded-full shadow-sm", bar)} style={{ width: `${w}%` }} />
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
        {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function labelDriver(x) {
  const name = cleanStr(x?.driverName ?? x?.name);
  const phone = cleanStr(x?.driverPhone ?? x?.phone);
  const left = name || "—";
  return phone ? `${left} • ${phone}` : left;
}

function SimpleBarList({ items = [], valueKey = "value", rightRenderer, tone = "blue" }) {
  const maxV = Math.max(1, ...items.map((x) => num(x?.[valueKey])));

  return (
    <div className="space-y-2">
      {items.map((x, idx) => {
        const v = num(x?.[valueKey]);
        const value01 = v / maxV;

        return (
          <div key={idx} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-700">
                  {idx + 1}
                </span>
                <div className="text-sm font-semibold text-slate-900 truncate">{labelDriver(x)}</div>
              </div>

              <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                {rightRenderer ? rightRenderer(x) : v.toLocaleString("en-IN")}
              </div>
            </div>

            <Meter value01={value01} tone={tone} />
          </div>
        );
      })}

      {items.length === 0 ? <div className="text-sm text-slate-500">No data.</div> : null}
    </div>
  );
}

/* ✅ Your exact pagination footer style */
function PaginationFooter({ page, size, totalPages, totalItems, onPrev, onNext, onSize }) {
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Showing <span className="font-semibold text-slate-700">{size}</span> • Total{" "}
          <span className="font-semibold text-slate-700">{totalItems}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 whitespace-nowrap">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                ▼
              </span>
            </div>
          </div>

          <div className="text-sm text-slate-700 whitespace-nowrap">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={!canNext}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- CSV export --------------------- */

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}
function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- status mappings --------------------- */

function complianceTone(st) {
  const s = safeStr(st).toUpperCase();
  if (s === "APPROVED_VERIFIED") return "green";
  if (s === "ACTION_REQUIRED") return "red";
  if (s === "PENDING_ADMIN_REVIEW" || s === "SUBMITTED") return "amber";
  return "slate";
}
function complianceLabel(st) {
  const s = safeStr(st).toUpperCase();
  return s ? s.replaceAll("_", " ") : "DRAFT";
}
function docsTone(complete, verified) {
  if (complete && verified) return "green";
  if (complete && !verified) return "amber";
  return "red";
}
function docsLabel(complete, verified) {
  const c = complete ? "COMPLETE" : "INCOMPLETE";
  const v = verified ? "VERIFIED" : "UNVERIFIED";
  return `${c} · ${v}`;
}

/* --------------------- page --------------------- */

export default function DirectoryDrivers() {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [status, setStatus] = useState("ALL"); // compliance status
  const [risk, setRisk] = useState("ALL"); // ATTENTION only
  const [link, setLink] = useState("ALL"); // LINKED/UNLINKED

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resp, setResp] = useState(null);

  const fetchSeq = useRef(0);

  // reset page when filters/search changes
  useEffect(() => {
    setPage(0);
  }, [qDebounced, status, risk, link, size]);

  useEffect(() => {
    let alive = true;
    const seq = ++fetchSeq.current;

    async function run() {
      setLoading(true);
      setErr("");

      try {
        const data = await fetchDirectoryDrivers({
          q: qDebounced,
          status: status === "ALL" ? undefined : status,
          risk: risk === "ALL" ? undefined : risk,
          link: link === "ALL" ? undefined : link,
          page,
          size,
        });

        if (!alive || seq !== fetchSeq.current) return;
        setResp(data || null);
      } catch (e) {
        if (!alive || seq !== fetchSeq.current) return;
        setErr(e?.message || "Failed to load drivers");
        setResp(null);
      } finally {
        if (!alive || seq !== fetchSeq.current) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [qDebounced, status, risk, link, page, size]);

  const summary = resp?.summary || null;
  const rows = resp?.items || [];
  const totalItems = num(resp?.totalItems);
  const totalPages = num(resp?.totalPages);
  const currentPage = num(resp?.page);
  const pageSize = num(resp?.size);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
    const filename = `drivers-directory-page-${currentPage + 1}-${stamp}.csv`;

    const header = [
      "DriverId",
      "Name",
      "Phone",
      "OwnerId",
      "OwnerName",
      "ComplianceStatus",
      "DocumentsComplete",
      "DocumentsVerified",
      "Bookings",
      "Rides",
      "ActiveRides",
      "Cancels",
      "CancelRate",
      "LastActivityAt",
    ];

    const lines = [header.map(csvEscape).join(",")];
    rows.forEach((r) => {
      lines.push(
        [
          r.id,
          r.name,
          r.phone,
          r.ownerId ?? "",
          r.ownerName ?? "",
          r.complianceStatus ?? "DRAFT",
          r.documentsComplete ? "YES" : "NO",
          r.documentsVerified ? "YES" : "NO",
          num(r.bookingCount),
          num(r.rideCount),
          num(r.activeRideCount),
          num(r.cancellationCount),
          num(r.cancellationRate),
          r.lastActivityAt || "",
        ]
          .map(csvEscape)
          .join(",")
      );
    });

    downloadTextFile(filename, lines.join("\n"));
  };

  const topBlock = useMemo(() => {
    if (!summary) return null;

    const totalDrivers = num(summary.totalDrivers).toLocaleString("en-IN");
    const linked = num(summary.linkedDrivers).toLocaleString("en-IN");
    const unlinked = num(summary.unlinkedDrivers).toLocaleString("en-IN");

    const docsComplete = num(summary.documentsCompleteDrivers).toLocaleString("en-IN");
    const docsVerified = num(summary.documentsVerifiedDrivers).toLocaleString("en-IN");

    const compVerified = num(summary.complianceVerifiedDrivers).toLocaleString("en-IN");
    const compAR = num(summary.complianceActionRequiredDrivers).toLocaleString("en-IN");

    const bookings7d = num(summary.bookings7d).toLocaleString("en-IN");
    const rides7d = num(summary.rides7d).toLocaleString("en-IN");

    const cancRate = num(summary.cancellationRate);
    const cancHint = `${num(summary.cancelledBookingsAllTime).toLocaleString("en-IN")} / ${num(
      summary.totalBookingsAllTime
    ).toLocaleString("en-IN")} lifetime`;

    return (
      <div className="space-y-4">
        {/* ✅ Consistent grid per row: 2 on mobile, 3 on md, 6 on lg+ */}
     {/* ✅ 10 KPIs → 5 per row (2 rows) */}
<div className="grid grid-cols-2 md:grid-cols-5 gap-3">
  {/* row 1 */}
  <KpiCard title="Total drivers" value={totalDrivers} hint="All time" accent="blue" />
  <KpiCard title="Linked drivers" value={linked} hint="Has owner link" accent="green" />
  <KpiCard
    title="Unlinked drivers"
    value={unlinked}
    hint="No owner link"
    accent={num(summary.unlinkedDrivers) > 0 ? "amber" : "blue"}
  />
  <KpiCard title="Docs complete" value={docsComplete} hint="All time" accent="blue" />
  <KpiCard title="Docs verified" value={docsVerified} hint="All time" accent="green" />

  {/* row 2 */}
  <KpiCard title="Compliance verified" value={compVerified} hint="Approved" accent="green" />
  <KpiCard
    title="Action required"
    value={compAR}
    hint="Needs fix"
    accent={num(summary.complianceActionRequiredDrivers) > 0 ? "red" : "blue"}
  />
  <KpiCard title="Bookings (7d)" value={bookings7d} hint="Created in last 7 days" accent="blue" />
  <KpiCard title="Rides (7d)" value={rides7d} hint="Created in last 7 days" accent="blue" />
  <KpiCard
    title="Cancellation rate"
    value={pct01(cancRate, 0)}
    hint={cancHint}
    accent={cancRate >= 0.25 ? "amber" : "blue"}
  />
</div>


        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 xl:col-span-4">
            <ChartCard title="Top drivers by bookings" subtitle="Most bookings (all time)">
              <SimpleBarList
                items={(summary.topDriversByBookings || []).slice(0, 3)}
                valueKey="value"
                tone="blue"
              />
            </ChartCard>
          </div>

          <div className="col-span-12 xl:col-span-4">
            <ChartCard title="Top drivers by rides" subtitle="Most rides (all time)">
              <SimpleBarList
                items={(summary.topDriversByRides || []).slice(0, 3)}
                valueKey="value"
                tone="blue"
              />
            </ChartCard>
          </div>

          <div className="col-span-12 xl:col-span-4">
            <ChartCard title="Top cancellations" subtitle="Cancellation count + % per driver">
              <div className="space-y-2">
                {(summary.topDriversByCancellations || []).slice(0, 3).map((x, idx) => {
                  const cc = num(x?.cancelCount);
                  const cr = num(x?.cancelRate);
                  return (
                    <div key={idx} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-rose-50 text-[11px] font-bold text-rose-700 border border-rose-100">
                            {idx + 1}
                          </span>
                          <div className="text-sm font-semibold text-slate-900 truncate">{labelDriver(x)}</div>
                        </div>

                        <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                          {cc.toLocaleString("en-IN")} • {pct01(cr, 0)}
                        </div>
                      </div>

                      <Meter value01={cr} tone="red" />
                    </div>
                  );
                })}

                {(summary.topDriversByCancellations || []).length === 0 ? (
                  <div className="text-sm text-slate-500">No data.</div>
                ) : null}
              </div>
            </ChartCard>
          </div>
        </div>
      </div>
    );
  }, [summary]);

  const COLS = [
    { key: "id", label: "ID", cls: "px-6 py-3 w-[90px]" },
    { key: "name", label: "Driver", cls: "px-6 py-3 w-[260px]" },
    { key: "phone", label: "Phone", cls: "px-6 py-3 w-[170px]" },
    { key: "owner", label: "Owner", cls: "px-6 py-3 w-[240px]" },
    { key: "comp", label: "Compliance", cls: "px-6 py-3 w-[200px]" },
    { key: "docs", label: "Docs", cls: "px-6 py-3 w-[200px]" },
    { key: "bookings", label: "Bookings", cls: "px-6 py-3 w-[110px] text-right" },
    { key: "rides", label: "Rides", cls: "px-6 py-3 w-[150px] text-right" },
    { key: "cancels", label: "Cancels", cls: "px-6 py-3 w-[160px] text-right" },
    { key: "last", label: "Last activity", cls: "px-6 py-3 w-[220px]" },
    { key: "action", label: "Action", cls: "px-6 py-3 w-[150px]" },
  ];

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                Drivers Directory
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Search, filter, and open driver support profiles.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Chip tone="blue">READ ONLY</Chip>
              <Chip>GLOBAL</Chip>
            </div>
          </div>

          <div className="mt-5">{topBlock}</div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/40">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-[300px]">
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Driver id / name / phone / license"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Compliance</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="APPROVED_VERIFIED">APPROVED_VERIFIED</option>
                  <option value="PENDING_ADMIN_REVIEW">PENDING_ADMIN_REVIEW</option>
                  <option value="ACTION_REQUIRED">ACTION_REQUIRED</option>
                  <option value="SUBMITTED">SUBMITTED</option>
                  <option value="DRAFT">DRAFT</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Risk</div>
                <select
                  value={risk}
                  onChange={(e) => setRisk(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="ATTENTION">Attention only</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Owner link</div>
                <select
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="LINKED">Linked</option>
                  <option value="UNLINKED">Unlinked</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-xs text-slate-500 whitespace-nowrap">
                Showing: <span className="text-slate-800">{loading ? "…" : rows.length}</span> drivers
              </div>

              <button
                onClick={exportCsv}
                disabled={loading || rows.length === 0}
                className="inline-flex h-10 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
          </div>

          {err ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {err}
            </div>
          ) : null}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1550px] w-full">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                {COLS.map((c) => (
                  <th key={c.key} className={c.cls}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {COLS.map((c) => (
                      <td key={c.key} className="px-6 py-4">
                        <div className="h-4 w-full rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} className="px-6 py-10 text-sm text-slate-600">
                    No drivers found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const comp = safeStr(r.complianceStatus || "DRAFT").toUpperCase();
                  const dTone = docsTone(!!r.documentsComplete, !!r.documentsVerified);

                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{r.id}</td>

                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis max-w-[260px]">
                        {r.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis max-w-[170px]">
                        {r.phone || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[240px]">
                        {r.ownerId ? (
                          <NavLink
                            to={`/admin/support/owners/${encodeURIComponent(String(r.ownerId))}`}
                            className="font-semibold text-slate-900 hover:underline"
                          >
                            {r.ownerName || "Owner"}
                          </NavLink>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <Chip tone={complianceTone(comp)}>{complianceLabel(comp)}</Chip>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <Chip tone={dTone}>{docsLabel(!!r.documentsComplete, !!r.documentsVerified)}</Chip>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(r.bookingCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(r.rideCount).toLocaleString("en-IN")}
                        {num(r.activeRideCount) > 0 ? (
                          <span className="ml-2 text-xs text-slate-500">
                            ({num(r.activeRideCount).toLocaleString("en-IN")} active)
                          </span>
                        ) : null}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(r.cancellationCount).toLocaleString("en-IN")}{" "}
                        <span className="text-xs text-slate-500">({pct01(r.cancellationRate, 0)})</span>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {dtClean(r.lastActivityAt)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <NavLink
                          to={`/admin/support/drivers/${encodeURIComponent(String(r.id))}`}
                          className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                        >
                          Open
                        </NavLink>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          page={currentPage}
          size={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
          onSize={(n) => setSize(clamp(n, 10, 200))}
        />

        {summary?.generatedAt ? (
          <div className="px-6 py-3 text-xs text-slate-400">Generated at: {dtClean(summary.generatedAt)}</div>
        ) : null}
      </div>
    </div>
  );
}
